import { FullOrganizationQuery } from "#src/api/Organization.ts";
import LinkButton from "#src/components/buttons/LinkButton.tsx";
import UpdatePaymentForm from "#src/components/forms/UpdatePaymentForm.tsx";
import Container from "#src/components/layout/Container.tsx";
import PaymentTable from "#src/components/layout/PaymentTable.tsx";
import { ContactSupportModal } from "#src/components/modals/ContactSupportModal.tsx";
import InvoiceModel from "#src/models/InvoiceModel.ts";
import { useApi } from "#src/stores/Api.tsx";
import { useStore } from "#src/stores/Store.tsx";
import { checkOrganizationPaymentAlerts } from "#src/utils/checkOrganizationPaymentAlerts.ts";
import formatMoney from "#src/utils/formatMoney.ts";
import { modals } from "#src/utils/modals.ts";

interface Props {
  full?: boolean;
}

export function OrganizationPaymentAlert(props: Props) {
  const store = useStore();

  const fullOrganizationQuery = useApi(FullOrganizationQuery, {
    skip: !props.full,
    variables: {
      id: store.organization.id,
    },
  });

  const organization = fullOrganizationQuery.organization;

  function emailSupport() {
    modals.show(ContactSupportModal, {
      sendToSupport: true,
    });
  }

  if (
    !store.organization ||
    (props.full && !fullOrganizationQuery.organization)
  ) {
    return null;
  }

  const paymentAlerts = checkOrganizationPaymentAlerts({
    organization: store.organization,
  });

  // console.log("paymentAlerts", paymentAlerts);

  let content = null;

  if (paymentAlerts.hasBalanceAlert && !props.full) {
    content = (
      <div className="bg-dark mb-4 flex items-center gap-4 rounded-full py-2 pr-4 pl-2 text-white">
        <div className="text-organization flex h-6 w-6 items-center justify-center rounded-full bg-white text-center text-sm">
          <i className="far fa-credit-card" />
        </div>
        <div className="flex-1">
          Your account has a balance of $
          {formatMoney(store.organization.owner.balance)}.{" "}
          <LinkButton
            className="text-white underline"
            to={`/admin/account/plan`}
          >
            Verify your payment method
          </LinkButton>{" "}
          to continue using Captyn.
        </div>

        <div>
          <LinkButton onClick={emailSupport} className="text-white underline">
            Need help?
          </LinkButton>
        </div>
      </div>
    );
  }

  if (paymentAlerts.hasBalanceAlert && props.full) {
    const currentInvoice = organization.owner.invoices.reduce(
      (acc, i) => {
        const invoice = new InvoiceModel(i);

        if (invoice.getBalance() !== 0) {
          return new InvoiceModel({
            ...invoice,
            invoiceItems: acc.invoiceItems.concat(invoice.invoiceItems),
          });
        }

        return acc;
      },
      new InvoiceModel({ invoiceItems: [] })
    );

    console.log("currentInvoice", currentInvoice);

    return (
      <div className="flex-1">
        <div className="fixed top-0 right-0 left-0 z-20 h-[50px] bg-white shadow">
          <Container full>
            <div className="flex h-[50px] items-center">
              <img
                src="/img/captyn-logo.svg"
                alt="Captyn"
                width="91"
                height="16"
              />
            </div>
          </Container>
        </div>

        <Container className="pt-24 pb-20 sm:pt-20">
          <h1>Update payment method</h1>

          <div className="mb-8">
            <p>Thank you for using Captyn!</p>

            <p>
              In order to continue providing services, we are in need of an
              updated payment method for your {store.organization.title}{" "}
              account.
            </p>

            {organization.planContent && (
              <div className="mb-4">
                <div
                  dangerouslySetInnerHTML={{
                    __html: organization.planContent,
                  }}
                />
              </div>
            )}
          </div>

          {currentInvoice.invoiceItems.length > 0 && (
            <div className="mb-8">
              <h3>Summary</h3>
              <hr />

              <PaymentTable
                fullWidth
                invoice={currentInvoice}
                removeInvoiceItem={() => {}}
              />
            </div>
          )}

          <div className="mb-8">
            <h3>Payment Method</h3>

            <UpdatePaymentForm
              user={organization.owner}
              onNetworkAction={store.updateOrganization}
              organization={store.organization}
            />
          </div>
        </Container>
      </div>
    );
  }

  return content;
}
